import { Injectable } from '@angular/core';
import { AuthService } from '../auth.service';
import { Observable } from 'rxjs';
import { CompetitionSchoolActivityFilter, CompetitionSchoolActivityGroupedModel } from '../../models/competition/competition-activity.model';
import { CompetitionModel, CompetitionFilter, CompetitionGoalModel, CompetitionParticipantSchoolFilter, CompetitionParticipantSchoolModel } from '../../models/competition/competition.model';
import { SchoolModel } from '../../models/school/school.model';
import { map } from 'rxjs/operators';
import { ConsumerCompetitionStats } from '../../models/competition/consumer-competition-stats.model';
import { StringExtensionModel } from '../../models/extensions/string-extension.model';

@Injectable({
  providedIn: 'root'
})
export class AdminCompetitionService {

  constructor(private authSv: AuthService) {
  }

  // Competition CRUD Operations

  createCompetition(competition: CompetitionModel): Observable<CompetitionModel> {

    const body: any = {
      name: competition.name,
      translatedName: competition.translatedName,
      translatedDescription: competition.translatedDescription,
      startAt: competition.startAt,
      endAt: competition.endAt,
      isPublic: competition.isPublic
    };

    // Optional for every type, when unset there is no redeem deadline
    if (competition.redeemEndAt) {
      body.redeemEndAt = competition.redeemEndAt;
    }

    // Without a type the backend creates a multiSchool competition
    if (competition.isConsumer()) {
      body.type = competition.type;
      body.countries = competition.countries;
    }

    return this.authSv.post('/admin/competitions', body).pipe(map((response) => {
      return CompetitionModel.generateModel(response.data);
    }));

  }

  fetchCompetitions(filter?: CompetitionFilter): Observable<CompetitionModel[]> {

    let url = '/admin/competitions';

    if (filter) {
      url += StringExtensionModel.queryString(filter);
    }

    return this.authSv.get(url).pipe(map((response) => {
      return CompetitionModel.generateModels(response.data);
    }));

  }

  fetchCompetitionById(competitionId: string): Observable<CompetitionModel> {

    return this.authSv.get(`/admin/competitions/${competitionId}`).pipe(map((response) => {
      return CompetitionModel.generateModel(response.data);
    }));

  }

  updateCompetition(competition: CompetitionModel): Observable<CompetitionModel> {

    // The type is never sent, it cannot change
    const body: any = {
      name: competition.name,
      translatedName: competition.translatedName,
      translatedDescription: competition.translatedDescription,
      startAt: competition.startAt,
      endAt: competition.endAt,
      redeemEndAt: competition.redeemEndAt,
      isPublic: competition.isPublic
    };

    if (competition.isConsumer()) {
      body.countries = competition.countries;
    }

    return this.authSv.patch(`/admin/competitions/${competition._id}`, body).pipe(map((response) => {
      return CompetitionModel.generateModel(response.data);
    }));

  }

  duplicateCompetition(competitionId: string, overrides?: Partial<CompetitionModel>): Observable<CompetitionModel> {

    return this.authSv.post(`/admin/competitions/${competitionId}/duplicate`, overrides || {}).pipe(map((response) => {
      return CompetitionModel.generateModel(response.data);
    }));

  }

  fetchConsumerStats(competitionId: string): Observable<ConsumerCompetitionStats> {

    return this.authSv.get(`/admin/competitions/${competitionId}/consumerStats`).pipe(map((response) => {
      return ConsumerCompetitionStats.generateModel(response.data);
    }));

  }

  // Goal Management Operations

  addGoalToCompetition(competitionId: string, goal: CompetitionGoalModel): Observable<CompetitionModel> {

    return this.authSv.post(`/admin/competitions/${competitionId}/goals`, goal).pipe(map((response) => {
      return CompetitionModel.generateModel(response.data);
    }));

  }

  updateGoalInCompetition(competitionId: string, goalId: string, goal: CompetitionGoalModel): Observable<CompetitionModel> {

    return this.authSv.patch(`/admin/competitions/${competitionId}/goals/${goalId}`, goal).pipe(map((response) => {
      return CompetitionModel.generateModel(response.data);
    }));

  }

  removeGoalFromCompetition(competitionId: string, goalId: string): Observable<CompetitionModel> {

    return this.authSv.delete(`/admin/competitions/${competitionId}/goals/${goalId}`).pipe(map((response) => {
      return CompetitionModel.generateModel(response.data);
    }));

  }

  // Participant School Management Operations

  addParticipantSchool(competitionId: string, schoolId: string): Observable<any> {

    return this.authSv.post(`/admin/competitions/${competitionId}/participantSchools/${schoolId}`, {}).pipe(map((response) => {
      return response.data;
    }));

  }

  removeParticipantSchool(competitionId: string, schoolId: string): Observable<any> {

    return this.authSv.delete(`/admin/competitions/${competitionId}/participantSchools/${schoolId}`).pipe(map((response) => {
      return response.data;
    }));

  }

  fetchParticipantSchools(competitionId: string, filter?: CompetitionParticipantSchoolFilter): Observable<SchoolModel[] | CompetitionParticipantSchoolModel[]> {

    let url = `/admin/competitions/${competitionId}/participantSchools`;

    if (filter) {
      url += StringExtensionModel.queryString(filter);
    }

    return this.authSv.get(url).pipe(map((response) => {
      // If includeSchoolAsRoot=1, return SchoolModel array
      if (filter && filter.includeSchoolAsRoot) {
        return SchoolModel.generateList(response.data);
      }
      // Otherwise, return CompetitionParticipantSchoolModel array
      return CompetitionParticipantSchoolModel.generateModels(response.data);
    }));

  }

  fetchParticipantSchoolById(competitionId: string, schoolId: string): Observable<any> {

    return this.authSv.get(`/admin/competitions/${competitionId}/participantSchools/${schoolId}`).pipe(map((response) => {
      return response.data;
    }));

  }

  fetchSchoolActivitiesGroupedWeekly(competitionId: string, filter: CompetitionSchoolActivityFilter): Observable<CompetitionSchoolActivityGroupedModel[]> {

    filter.type = 'weekly';
    filter.group = true;

    const url = `/admin/competitions/${competitionId}/schoolActivities` + StringExtensionModel.queryString(filter);

    return this.authSv.get(url).pipe(map((response) => {
      return CompetitionSchoolActivityGroupedModel.generateModels(response.data);
    }));

  }

  // Convenience method to fetch participant schools as SchoolModel array (includeSchoolAsRoot=1)
  fetchParticipantSchoolsAsSchools(competitionId: string, filter?: CompetitionParticipantSchoolFilter): Observable<SchoolModel[]> {
    const schoolFilter = filter || new CompetitionParticipantSchoolFilter();
    schoolFilter.includeSchoolAsRoot = true;
    return this.fetchParticipantSchools(competitionId, schoolFilter) as Observable<SchoolModel[]>;
  }

  // Convenience method to fetch participant schools as CompetitionParticipantSchoolModel array with nested school data (includeSchool=1)
  fetchParticipantSchoolsWithSchoolData(competitionId: string, filter?: CompetitionParticipantSchoolFilter): Observable<CompetitionParticipantSchoolModel[]> {
    const participantFilter = filter || new CompetitionParticipantSchoolFilter();
    participantFilter.includeSchool = true;
    return this.fetchParticipantSchools(competitionId, participantFilter) as Observable<CompetitionParticipantSchoolModel[]>;
  }

  // Convenience method to fetch participant schools as CompetitionParticipantSchoolModel array without nested school data (default behavior)
  fetchParticipantSchoolsAsParticipants(competitionId: string, filter?: CompetitionParticipantSchoolFilter): Observable<CompetitionParticipantSchoolModel[]> {
    const participantFilter = filter || new CompetitionParticipantSchoolFilter();
    participantFilter.includeSchool = false;
    participantFilter.includeSchoolAsRoot = false;
    return this.fetchParticipantSchools(competitionId, participantFilter) as Observable<CompetitionParticipantSchoolModel[]>;
  }

}

